import { useTranslation } from 'react-i18next';

import {
  type HistoryDetailColumn,
  type HistoryDetailItem,
  type HistoryEvent,
  useHistoryDetails,
} from '@/entities/database';
import { useViewer } from '@/entities/viewer';
import { Button, Dialog, ErrorState, LoadingState } from '@/shared/ui';

import { type Context, show } from './describe';
import styles from './History.module.css';

interface Props {
  databaseId: string;
  event: HistoryEvent;
  /** Narrow the entry to this record's own changes. */
  recordId?: string | null;
  text: string;
  onClose: () => void;
}

const shortId = (id: string) => id.slice(0, 8);

/**
 * Every stored change of a bulk entry (a big formatting, an import, a copy), read a chunk at a
 * time from the server - the timeline itself never holds them. Values are the stored ones.
 */
export function HistoryDetailsDialog({ databaseId, event, recordId = null, text, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const viewer = useViewer();
  const timeZone = viewer.status === 'authenticated' ? viewer.user.timezone : undefined;
  const query = useHistoryDetails(databaseId, event.id, recordId);
  const pages = query.data?.pages ?? [];
  const first = pages[0];
  const columns = new Map<string, HistoryDetailColumn>(
    (first?.columns ?? []).map((c) => [c.id, c]),
  );
  const users = Object.assign({}, ...pages.map((page) => page.users)) as Record<string, string>;
  const ctx: Context = { t, locale: i18n.language, timeZone, users };
  const items = pages.flatMap((page) => page.items);

  const value = (columnId: string, raw: unknown) => {
    const column = columns.get(columnId);
    const options = column?.options;
    const label =
      options && typeof raw === 'string'
        ? options[raw]
        : options && Array.isArray(raw)
          ? raw.map((v) => options[String(v)] ?? '')
          : undefined;
    const names =
      column?.field_type === 'user'
        ? Array.isArray(raw)
          ? raw.map((v) => users[String(v)] ?? '')
          : users[String(raw)]
        : undefined;
    return show(ctx, raw, column?.field_type, label ?? names);
  };

  const row = (item: HistoryDetailItem, index: number) => {
    if (item.cells) {
      return (
        <li key={`${item.record_id}-${String(index)}`} className={styles.detailItem}>
          <strong>{t('databases.history.detailsRecord', { id: shortId(item.record_id) })}</strong>
          {Object.entries(item.cells).map(([columnId, raw]) => (
            <span key={columnId} className={styles.line}>
              <span className={styles.label}>{columns.get(columnId)?.name ?? columnId}</span>
              <span>{value(columnId, raw)}</span>
            </span>
          ))}
        </li>
      );
    }
    const column = item.column_id ? columns.get(item.column_id) : undefined;
    return (
      <li
        key={`${item.record_id}-${item.column_id ?? ''}-${item.property ?? ''}-${String(index)}`}
        className={styles.detailItem}
      >
        <strong>{t('databases.history.detailsRecord', { id: shortId(item.record_id) })}</strong>
        <span className={styles.line}>
          <span className={styles.label}>
            {column?.name ?? ''}{' '}
            {item.property === 'wrap'
              ? t('databases.history.fields.wrap')
              : t('databases.history.fields.color')}
          </span>
          <span className={styles.was}>{show(ctx, item.before, undefined)}</span>
          <span aria-hidden="true"> → </span>
          <span className={styles.now}>{show(ctx, item.after, undefined)}</span>
        </span>
      </li>
    );
  };

  return (
    <Dialog title={t('databases.history.detailsTitle')} size="xl" onClose={onClose}>
      <div className={styles.detail} data-testid="history-details">
        <p className={styles.muted}>{text}</p>
        {query.isPending && <LoadingState />}
        {query.isError && <ErrorState error={query.error} onRetry={() => void query.refetch()} />}
        {items.length > 0 && (
          <ol className={styles.list} data-testid="history-detail-list">
            {items.map(row)}
          </ol>
        )}
        {query.hasNextPage && (
          <Button
            variant="secondary"
            disabled={query.isFetchingNextPage}
            onClick={() => void query.fetchNextPage()}
          >
            {query.isFetchingNextPage
              ? t('databases.history.detailsLoading')
              : t('databases.history.detailsMore')}
          </Button>
        )}
      </div>
    </Dialog>
  );
}
